import { useEffect, useMemo, useRef, useState } from "react";
import { skipToken } from "@reduxjs/toolkit/query";
import {
  InvoicePaymentInstrumentKind,
  InvoicePaymentMethodSourceType,
  useLazyGetHarnessInvoiceReceiptHtmlQuery,
  useGetHarnessInvoiceQuery,
  useGetHarnessInvoicesQuery,
  useLazyGetHarnessInvoiceHtmlQuery,
  usePayHarnessInvoiceMutation,
} from "@/api/providers/invoices"
import { AchPaymentFields } from "@/plugins/controls/common/ach-payment-fields"
import {
  useGetHarnessPaymentCountriesQuery,
  useGetHarnessPaymentStatesQuery,
  useGetHarnessCustomerPaymentMethodsQuery,
  useGetHarnessPaymentTokenizationDetailQuery,
} from "@/api/providers/payment-methods";
import { useGetHarnessSubscriptionsQuery } from "@/api/providers/subscriptions";
import type { HarnessInvoiceStatus } from "@/api/providers/invoices";
import { Button } from "@/common/elements/button";
import { Skeleton } from "@/common/elements/skeleton";
import { toast } from "@/common/elements/sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/common/elements/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/common/elements/sheet";
import { useNavigation } from "@/common/hooks/use-navigation";
import { useSelectedCustomerId } from "@/common/hooks/use-selected-customer-id";
import { ApiSession } from "@/common/auth/utils";
import { AuthenticatedUser } from "@/common/keycloak/keycloak";
import {
  UiPaymentMethodType,
  UiInvoiceStatus,
  formatPaymentMethodSummary,
  getInvoiceStatusDisplay,
  mapHarnessInvoiceToUi,
  mapHarnessPaymentMethodToUi,
  mapHarnessSubscriptionToUi,
  type UiInvoice,
} from "@/plugins/controls/common/billing-mappers";
import {
  UNSUPPORTED_UNKNOWN_CARD_MESSAGE,
  calculateSurchargeAmountCents,
  isCreditCard,
  isUnknownCard,
  normalizeAchAccountHolderType,
  normalizeAchAccountType,
  normalizeCardCategoryKnown,
  parseAllowUnknownCardTypeProcessing,
  parsePositiveSurchargePercent,
  toDollarsFromCents,
  type AchAccountHolderType,
  type AchAccountType,
  type CardCategory,
} from "@/plugins/controls/common/billing-payment-helpers";
import {
  createEmptyBillingAddressForm,
  getDefaultPaymentCountryId,
  normalizePostalCode,
  validateBillingAddressFields,
  type BillingAddressFormFields,
  type BillingAddressValidationErrors,
} from "@/plugins/controls/common/billing-address";
import { BillingAddressFields } from "@/plugins/controls/common/billing-address-fields";
import {
  CollectJsPaymentForm,
  type CollectJsTokenizedPayment,
} from "@/plugins/controls/payment-methods-control/payment-methods-drawer/collect-js-payment-form/CollectJsPaymentForm";
import { buildInvoicePdfFromHtml, sanitizePdfFileName } from "./invoice-pdf";
import { PaymentMethodTypeEnum } from "@/plugins/stub/billing-data/utils";
import { formatCurrency, formatDate } from "@/plugins/stub/billing-data/utils";

enum InvoiceFilter {
  All = "all",
  Open = "open",
  Paid = "paid",
  Overdue = "overdue",
}

const ALL_SUBSCRIPTIONS_FILTER = "all";
const PAY_REQUEST_TIMEOUT_MS = 60000;
const RECEIPT_NOT_FOUND_MESSAGE =
  "Unexpected issue getting your receipt. Please reach out to payments@cloudfitsoftware.com for assistance.";

enum PaymentSourceMode {
  Saved = "saved",
  New = "new",
}

function toNullableString(value: string) {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function formatDateOrFallback(dateText: string | null) {
  return dateText ? formatDate(dateText) : "N/A";
}

function matchesStatusFilter(
  invoice: UiInvoice,
  selectedFilter: InvoiceFilter,
) {
  return (
    selectedFilter === InvoiceFilter.All ||
    (selectedFilter === InvoiceFilter.Open &&
      (invoice.status === UiInvoiceStatus.Due ||
        invoice.status === UiInvoiceStatus.Overdue)) ||
    (selectedFilter === InvoiceFilter.Paid &&
      invoice.status === UiInvoiceStatus.Paid) ||
    (selectedFilter === InvoiceFilter.Overdue &&
      invoice.status === UiInvoiceStatus.Overdue)
  );
}

export function InvoicesControl(_props: {
  authUser: AuthenticatedUser;
  apiSession: ApiSession | null;
  onLogout: () => Promise<void>;
}) {
  const selectedCustomerId = useSelectedCustomerId();
  const { searchParams, setSearchParams } = useNavigation();
  const selectedSubscriptionId =
    searchParams.get("subscription") ?? ALL_SUBSCRIPTIONS_FILTER;
  const selectedFilter = (searchParams.get("status") ??
    InvoiceFilter.All) as InvoiceFilter;

  const subscriptionQueryArg = selectedCustomerId
    ? { customerId: selectedCustomerId }
    : skipToken;
  const invoiceQueryArg = useMemo(() => {
    if (!selectedCustomerId) {
      return skipToken;
    }

    const invoiceStatusFilter: HarnessInvoiceStatus[] | undefined =
      selectedFilter === InvoiceFilter.Paid
        ? ["Paid"]
        : selectedFilter === InvoiceFilter.Open ||
            selectedFilter === InvoiceFilter.Overdue
          ? ["Open"]
          : undefined;

    return {
      customerId: selectedCustomerId,
      subscriptionIds:
        selectedSubscriptionId === ALL_SUBSCRIPTIONS_FILTER
          ? undefined
          : [selectedSubscriptionId],
      invoiceStatus: invoiceStatusFilter,
    };
  }, [selectedCustomerId, selectedFilter, selectedSubscriptionId]);

  const {
    data: subscriptionData = [],
    isLoading: isSubscriptionsLoading,
    isFetching: isSubscriptionsFetching,
  } = useGetHarnessSubscriptionsQuery(subscriptionQueryArg);
  const {
    data: invoiceData = [],
    isLoading: isInvoicesLoading,
    isFetching: isInvoicesFetching,
    refetch: refetchInvoices,
  } = useGetHarnessInvoicesQuery(invoiceQueryArg);
  const paymentMethodsQueryArg = selectedCustomerId
    ? { customerId: selectedCustomerId }
    : skipToken;
  const {
    data: savedPaymentMethodData = [],
    refetch: refetchSavedPaymentMethods,
  } = useGetHarnessCustomerPaymentMethodsQuery(paymentMethodsQueryArg);
  const { data: tokenizationDetail, isLoading: tokenizationLoading } =
    useGetHarnessPaymentTokenizationDetailQuery();
  const {
    data: paymentCountries = [],
    isLoading: paymentCountriesLoading,
    isFetching: paymentCountriesFetching,
  } = useGetHarnessPaymentCountriesQuery();
  const [payHarnessInvoice, { isLoading: isPayingInvoice }] =
    usePayHarnessInvoiceMutation();

  const [payInvoice, setPayInvoice] = useState<UiInvoice | null>(null)
  const [paymentSourceMode, setPaymentSourceMode] = useState<PaymentSourceMode>(PaymentSourceMode.Saved)
  const [selectedSavedPaymentMethodId, setSelectedSavedPaymentMethodId] = useState<number | null>(null)
  const [newMethodType, setNewMethodType] = useState(PaymentMethodTypeEnum.Card)
  const [newAchAccountType, setNewAchAccountType] = useState<AchAccountType | null>(null)
  const [newAchAccountHolderType, setNewAchAccountHolderType] = useState<AchAccountHolderType | null>(null)
  const [saveAndSetDefault, setSaveAndSetDefault] = useState(true)
  const [collectTokenRequestNonce, setCollectTokenRequestNonce] = useState(0)
  const [collectError, setCollectError] = useState<string | null>(null)
  const [newMethodCardCategory, setNewMethodCardCategory] = useState<CardCategory | null>(null)
  const [paySubmitPending, setPaySubmitPending] = useState(false)
  const [billingAddressValidationErrors, setBillingAddressValidationErrors] = useState<BillingAddressValidationErrors>({})
  const [pdfGeneratingInvoiceId, setPdfGeneratingInvoiceId] = useState<string | null>(null)
  const [receiptPdfGeneratingInvoiceId, setReceiptPdfGeneratingInvoiceId] = useState<string | null>(null)
  const paySubmitPendingRef = useRef(false)
  const paySubmitTimeoutRef = useRef<number | null>(null)
  const [billingAddressForm, setBillingAddressForm] = useState<BillingAddressFormFields>(createEmptyBillingAddressForm)
  const [nickname, setNickname] = useState<string | null>("");

  const paymentStatesQueryArg =
    payInvoice !== null &&
    paymentSourceMode === PaymentSourceMode.New &&
    billingAddressForm.countryId !== null
      ? { countryId: billingAddressForm.countryId }
      : skipToken;
  const {
    data: paymentStates = [],
    isLoading: paymentStatesLoading,
    isFetching: paymentStatesFetching,
  } = useGetHarnessPaymentStatesQuery(paymentStatesQueryArg);

  const invoiceDetailQueryArg =
    selectedCustomerId && payInvoice
      ? {
          customerId: selectedCustomerId,
          invoiceId: payInvoice.providerInvoiceId,
        }
      : skipToken;
  const {
    data: invoiceDetailData,
    isLoading: isInvoiceDetailLoading,
    isFetching: isInvoiceDetailFetching,
  } = useGetHarnessInvoiceQuery(invoiceDetailQueryArg);
  const [getHarnessInvoiceHtml] = useLazyGetHarnessInvoiceHtmlQuery();
  const [getHarnessInvoiceReceiptHtml] =
    useLazyGetHarnessInvoiceReceiptHtmlQuery();

  const isTableLoading =
    isSubscriptionsLoading ||
    isSubscriptionsFetching ||
    isInvoicesLoading ||
    isInvoicesFetching;
  const shouldShowTableLoading = selectedCustomerId !== null && isTableLoading;

  const subscriptions = useMemo(
    () =>
      subscriptionData.map((subscription, index) =>
        mapHarnessSubscriptionToUi(subscription, index),
      ),
    [subscriptionData],
  );
  const invoices = useMemo(
    () =>
      invoiceData.map((invoice, index) =>
        mapHarnessInvoiceToUi(invoice, index),
      ),
    [invoiceData],
  );
  const activePayInvoice = useMemo(
    () =>
      invoiceDetailData
        ? mapHarnessInvoiceToUi(invoiceDetailData, 0)
        : payInvoice,
    [invoiceDetailData, payInvoice],
  );
  const savedPaymentMethods = useMemo(
    () =>
      savedPaymentMethodData
        .map((method) => mapHarnessPaymentMethodToUi(method))
        .filter((method) => !method.isDisabled),
    [savedPaymentMethodData],
  );
  const defaultSavedMethodId = useMemo(
    () =>
      savedPaymentMethods.find((method) => method.isDefault)?.id ??
      savedPaymentMethods[0]?.id ??
      null,
    [savedPaymentMethods],
  );
  const defaultCountryId = useMemo(
    () => getDefaultPaymentCountryId(paymentCountries),
    [paymentCountries],
  );
  const selectedSavedPaymentMethod = useMemo(
    () =>
      savedPaymentMethods.find(
        (method) => method.id === selectedSavedPaymentMethodId,
      ) ?? null,
    [savedPaymentMethods, selectedSavedPaymentMethodId],
  );
  const surchargePercent = useMemo(() => {
    const candidate = tokenizationDetail?.creditCardSurchargePercent ?? null;
    return parsePositiveSurchargePercent(candidate);
  }, [tokenizationDetail]);
  const allowUnknownCardTypeProcessing = parseAllowUnknownCardTypeProcessing(
    tokenizationDetail?.allowUnknownCardTypeProcessing,
  );
  const isSavedCreditCardSelected =
    paymentSourceMode === PaymentSourceMode.Saved &&
    selectedSavedPaymentMethod?.type === UiPaymentMethodType.Card &&
    isCreditCard(
      normalizeCardCategoryKnown(selectedSavedPaymentMethod.cardCategory),
    );
  const isNewCreditCardSelected =
    paymentSourceMode === PaymentSourceMode.New &&
    newMethodType === PaymentMethodTypeEnum.Card &&
    isCreditCard(normalizeCardCategoryKnown(newMethodCardCategory));
  const isSavedAchSelected =
    paymentSourceMode === PaymentSourceMode.Saved &&
    selectedSavedPaymentMethod?.type === UiPaymentMethodType.Ach;
  const isNewAchSelected =
    paymentSourceMode === PaymentSourceMode.New &&
    newMethodType === PaymentMethodTypeEnum.Ach;
  const isAchPaymentSelected = isSavedAchSelected || isNewAchSelected;
  const shouldShowAchRecurringAuthorization =
    isNewAchSelected && saveAndSetDefault;
  const shouldApplySurcharge =
    isSavedCreditCardSelected || isNewCreditCardSelected;
  const amountDueCents = activePayInvoice?.amountCents ?? 0;
  const surchargeAmountCents = shouldApplySurcharge
    ? calculateSurchargeAmountCents(amountDueCents, surchargePercent)
    : 0;
  const totalChargeCents = amountDueCents + surchargeAmountCents;
  const currentDateLabel = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date());

  const filteredInvoices = useMemo(() => {
    return invoices.filter((invoice) => {
      return matchesStatusFilter(invoice, selectedFilter);
    });
  }, [invoices, selectedFilter]);

  useEffect(() => {
    paySubmitPendingRef.current = paySubmitPending;
  }, [paySubmitPending]);

  useEffect(() => {
    if (!payInvoice) {
      return;
    }

    if (
      selectedSavedPaymentMethodId !== null &&
      savedPaymentMethods.some(
        (method) => method.id === selectedSavedPaymentMethodId,
      )
    ) {
      return;
    }

    setSelectedSavedPaymentMethodId(defaultSavedMethodId);
  }, [
    defaultSavedMethodId,
    payInvoice,
    savedPaymentMethods,
    selectedSavedPaymentMethodId,
  ]);

  useEffect(() => {
    if (
      payInvoice === null ||
      paymentSourceMode !== PaymentSourceMode.New ||
      billingAddressForm.countryId !== null ||
      defaultCountryId === null
    ) {
      return;
    }

    setBillingAddressForm((current) => ({
      ...current,
      countryId: defaultCountryId,
    }));
  }, [
    billingAddressForm.countryId,
    defaultCountryId,
    payInvoice,
    paymentSourceMode,
  ]);

  useEffect(() => {
    if (
      payInvoice === null ||
      paymentSourceMode !== PaymentSourceMode.New ||
      billingAddressForm.stateId === null
    ) {
      return;
    }

    if (
      paymentStates.some((state) => state.id === billingAddressForm.stateId)
    ) {
      return;
    }

    setBillingAddressForm((current) => ({ ...current, stateId: null }));
  }, [
    billingAddressForm.stateId,
    payInvoice,
    paymentSourceMode,
    paymentStates,
  ]);

  function updateBillingAddressForm(nextValue: BillingAddressFormFields) {
    setBillingAddressForm(nextValue);
    setBillingAddressValidationErrors({});
  }

  useEffect(() => {
    return () => {
      if (paySubmitTimeoutRef.current !== null) {
        window.clearTimeout(paySubmitTimeoutRef.current);
      }
    };
  }, []);

  function clearPaySubmitTimeout() {
    if (paySubmitTimeoutRef.current !== null) {
      window.clearTimeout(paySubmitTimeoutRef.current);
      paySubmitTimeoutRef.current = null;
    }
  }

  function resetPayDrawerState() {
    clearPaySubmitTimeout()
    setPayInvoice(null)
    setPaymentSourceMode(savedPaymentMethods.length > 0 ? PaymentSourceMode.Saved : PaymentSourceMode.New)
    setNewAchAccountType(null)
    setNewAchAccountHolderType(null)
    setCollectError(null)
    setNewMethodCardCategory(null)
    setPaySubmitPending(false)
    setBillingAddressValidationErrors({})
    setSaveAndSetDefault(true)
    setNewMethodType(PaymentMethodTypeEnum.Card)
    setBillingAddressForm(createEmptyBillingAddressForm())
    setNickname("");
  }

  function openPayDrawer(invoice: UiInvoice) {
    clearPaySubmitTimeout()
    setPayInvoice(invoice)
    setNewAchAccountType(null)
    setNewAchAccountHolderType(null)
    setCollectError(null)
    setNewMethodCardCategory(null)
    setPaySubmitPending(false)
    setBillingAddressValidationErrors({})
    setPaymentSourceMode(savedPaymentMethods.length > 0 ? PaymentSourceMode.Saved : PaymentSourceMode.New)
    setSelectedSavedPaymentMethodId(defaultSavedMethodId)
  }

  function isNotFoundError(error: unknown) {
    if (typeof error !== "object" || error === null || !("status" in error)) {
      return false;
    }

    return (error as { status?: unknown }).status === 404;
  }

  function getReceiptErrorMessage(error: unknown, fallback: string) {
    if (isNotFoundError(error)) {
      return RECEIPT_NOT_FOUND_MESSAGE;
    }

    return error instanceof Error ? error.message : fallback;
  }

  async function executeInvoicePayment(
    sourceType: InvoicePaymentMethodSourceType,
    paymentToken?: string,
    tokenizedPayment?: CollectJsTokenizedPayment,
  ) {
    if (!selectedCustomerId || !activePayInvoice) {
      return;
    }

    const isNewPaymentMethod =
      sourceType === InvoicePaymentMethodSourceType.NewPaymentMethod;
    const rawSelectedSavedPaymentMethod =
      sourceType === InvoicePaymentMethodSourceType.SavedPaymentMethod
        ? (savedPaymentMethodData.find(
            (method) => method.id === selectedSavedPaymentMethodId,
          ) ?? null)
        : null;
    const instrumentKind =
      sourceType === InvoicePaymentMethodSourceType.SavedPaymentMethod
        ? selectedSavedPaymentMethod?.type === UiPaymentMethodType.Ach
          ? InvoicePaymentInstrumentKind.Ach
          : InvoicePaymentInstrumentKind.Card
        : newMethodType === PaymentMethodTypeEnum.Ach
          ? InvoicePaymentInstrumentKind.Ach
          : InvoicePaymentInstrumentKind.Card;
    const tokenizedCardType =
      isNewPaymentMethod && instrumentKind === InvoicePaymentInstrumentKind.Card
        ? toNullableString(tokenizedPayment?.cardBrand ?? "")
        : null;
    const tokenizedCardCategory =
      isNewPaymentMethod && instrumentKind === InvoicePaymentInstrumentKind.Card
        ? toNullableString(tokenizedPayment?.cardCategory ?? "")
        : null;
    const tokenizedMaskedAccountNumber = isNewPaymentMethod
      ? instrumentKind === InvoicePaymentInstrumentKind.Card
        ? toNullableString(tokenizedPayment?.cardLast4 ?? "")
        : toNullableString(tokenizedPayment?.achAccountLast4 ?? "")
      : null;
    const tokenizedExpirationDate =
      isNewPaymentMethod && instrumentKind === InvoicePaymentInstrumentKind.Card
        ? toNullableString(
            [
              tokenizedPayment?.cardExpMonth ?? "",
              tokenizedPayment?.cardExpYear ?? "",
            ]
              .filter((part) => part && part.length > 0)
              .join("/"),
          )
        : null
    const savedCardType =
      sourceType === InvoicePaymentMethodSourceType.SavedPaymentMethod &&
      selectedSavedPaymentMethod?.type === UiPaymentMethodType.Card
        ? toNullableString(selectedSavedPaymentMethod.brand ?? "")
        : null;
    const savedCardCategory =
      sourceType === InvoicePaymentMethodSourceType.SavedPaymentMethod &&
      selectedSavedPaymentMethod?.type === UiPaymentMethodType.Card
        ? toNullableString(selectedSavedPaymentMethod.cardCategory ?? "")
        : null;
    const savedMaskedAccountNumber =
      sourceType === InvoicePaymentMethodSourceType.SavedPaymentMethod
        ? toNullableString(
            rawSelectedSavedPaymentMethod?.maskedAccountNumber ?? "",
          )
        : null;
    const savedExpirationDate =
      sourceType === InvoicePaymentMethodSourceType.SavedPaymentMethod
        ? toNullableString(rawSelectedSavedPaymentMethod?.expirationDate ?? "")
        : null
    const savedAccountType =
      sourceType === InvoicePaymentMethodSourceType.SavedPaymentMethod
        ? normalizeAchAccountType(rawSelectedSavedPaymentMethod?.accountType)
        : null
    const savedAccountHolderType =
      sourceType === InvoicePaymentMethodSourceType.SavedPaymentMethod
        ? normalizeAchAccountHolderType(rawSelectedSavedPaymentMethod?.accountHolderType)
        : null
    const achAccountType =
      instrumentKind === InvoicePaymentInstrumentKind.Ach
        ? isNewPaymentMethod
          ? newAchAccountType
          : savedAccountType
        : null
    const achAccountHolderType =
      instrumentKind === InvoicePaymentInstrumentKind.Ach
        ? isNewPaymentMethod
          ? newAchAccountHolderType
          : savedAccountHolderType
        : null

    if (instrumentKind === InvoicePaymentInstrumentKind.Ach && (achAccountType === null || achAccountHolderType === null)) {
      throw new Error("ACH account type and account holder type are required.")
    }

    const invoiceAmount = toDollarsFromCents(amountDueCents)
    const surchargeAmount = toDollarsFromCents(surchargeAmountCents)

    const response = await payHarnessInvoice({
      customerId: selectedCustomerId,
      invoiceId: activePayInvoice.providerInvoiceId,
      body: {
        paymentMethod: {
          sourceType,
          customerPaymentMethodId:
            sourceType === InvoicePaymentMethodSourceType.SavedPaymentMethod
              ? selectedSavedPaymentMethodId
              : null,
          instrumentKind,
          paymentToken: paymentToken ?? null,
          maskedAccountNumber: tokenizedMaskedAccountNumber ?? savedMaskedAccountNumber,
          expirationDate: tokenizedExpirationDate ?? savedExpirationDate,
          paymentMethodNickname:
            isNewPaymentMethod && saveAndSetDefault
              ? toNullableString(nickname ?? "")
              : null,
          achAccountType,
          achAccountHolderType,
          cardType: tokenizedCardType ?? savedCardType,
          cardCategory: tokenizedCardCategory ?? savedCardCategory,
          savePaymentMethod: isNewPaymentMethod ? saveAndSetDefault : false,
          makeDefaultPaymentMethod: isNewPaymentMethod ? saveAndSetDefault : false,
          ...(isNewPaymentMethod
            ? {
                billingAddress: {
                  firstName: toNullableString(billingAddressForm.firstName),
                  lastName: toNullableString(billingAddressForm.lastName),
                  company: toNullableString(billingAddressForm.company),
                  address1: toNullableString(billingAddressForm.address1),
                  address2: toNullableString(billingAddressForm.address2),
                  city: toNullableString(billingAddressForm.city),
                  postalCode: toNullableString(billingAddressForm.postalCode),
                  stateId: billingAddressForm.stateId,
                  countryId: billingAddressForm.countryId,
                  phone: toNullableString(billingAddressForm.phone),
                  fax: toNullableString(billingAddressForm.fax),
                },
              }
            : {}),
        },
        invoiceAmount,
        totalAmount: toDollarsFromCents(totalChargeCents),
        surchargeAmount,
      },
    }).unwrap();

    if (!response.isSuccess) {
      throw new Error(
        response.errorMessage ??
          response.responseMessage ??
          "Could not process this invoice payment.",
      );
    }

    if (
      isNewPaymentMethod &&
      saveAndSetDefault &&
      response.savedPaymentMethodCreated
    ) {
      await refetchSavedPaymentMethods();
    }

    await refetchInvoices();
    resetPayDrawerState();
    toast.success(
      response.responseMessage ?? "Invoice payment submitted successfully.",
    );
  }

  async function openInvoiceView(invoice: UiInvoice) {
    if (!selectedCustomerId) {
      toast.error("Select a customer before opening an invoice.");
      return;
    }

    const popup = window.open(
      "",
      "invoice-view",
      "popup=yes,width=1200,height=800",
    );

    if (!popup) {
      toast.error("Unable to open the invoice window.");
      return;
    }

    popup.document.open();
    popup.document.write(
      "<p style='font-family: sans-serif; padding: 16px;'>Loading invoice...</p>",
    );
    popup.document.close();

    try {
      const invoiceHtmlResponse = await getHarnessInvoiceHtml({
        customerId: selectedCustomerId,
        invoiceId: invoice.providerInvoiceId,
      }).unwrap();

      popup.document.open();
      popup.document.write(invoiceHtmlResponse.html);
      popup.document.close();
      popup.focus();
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Could not load the invoice HTML.";
      popup.document.open();
      popup.document.write(
        `<p style='font-family: sans-serif; padding: 16px;'>${message}</p>`,
      );
      popup.document.close();
      toast.error(message);
    }
  }

  async function downloadInvoicePdf(invoice: UiInvoice) {
    if (!selectedCustomerId) {
      toast.error("Select a customer before downloading an invoice PDF.");
      return;
    }

    setPdfGeneratingInvoiceId(invoice.providerInvoiceId);

    try {
      const invoiceHtmlResponse = await getHarnessInvoiceHtml({
        customerId: selectedCustomerId,
        invoiceId: invoice.providerInvoiceId,
      }).unwrap();

      const fileName = sanitizePdfFileName(`invoice-${invoice.id}.pdf`);
      await buildInvoicePdfFromHtml({
        html: invoiceHtmlResponse.html,
        pdfSizing: invoiceHtmlResponse.pdfSizing,
        fileName,
      });
      toast.success("Invoice PDF downloaded.");
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Could not generate an invoice PDF.";
      toast.error(message);
    } finally {
      setPdfGeneratingInvoiceId((current) =>
        current === invoice.providerInvoiceId ? null : current,
      );
    }
  }

  async function openReceiptView(invoice: UiInvoice) {
    if (!selectedCustomerId) {
      toast.error("Select a customer before opening a receipt.");
      return;
    }

    const popup = window.open(
      "",
      "receipt-view",
      "popup=yes,width=1200,height=800",
    );

    if (!popup) {
      toast.error("Unable to open the receipt window.");
      return;
    }

    popup.document.open();
    popup.document.write(
      "<p style='font-family: sans-serif; padding: 16px;'>Loading receipt...</p>",
    );
    popup.document.close();

    try {
      const receiptHtmlResponse = await getHarnessInvoiceReceiptHtml({
        customerId: selectedCustomerId,
        invoiceId: invoice.providerInvoiceId,
      }).unwrap();

      popup.document.open();
      popup.document.write(receiptHtmlResponse.html);
      popup.document.close();
      popup.focus();
    } catch (error) {
      const message = getReceiptErrorMessage(
        error,
        "Could not load the receipt HTML.",
      );
      popup.document.open();
      popup.document.write(
        `<p style='font-family: sans-serif; padding: 16px;'>${message}</p>`,
      );
      popup.document.close();
      toast.error(message);
    }
  }

  async function downloadReceiptPdf(invoice: UiInvoice) {
    if (!selectedCustomerId) {
      toast.error("Select a customer before downloading a receipt PDF.");
      return;
    }

    setReceiptPdfGeneratingInvoiceId(invoice.providerInvoiceId);

    try {
      const receiptHtmlResponse = await getHarnessInvoiceReceiptHtml({
        customerId: selectedCustomerId,
        invoiceId: invoice.providerInvoiceId,
      }).unwrap();

      const fileName = sanitizePdfFileName(`receipt-${invoice.id}.pdf`);
      await buildInvoicePdfFromHtml({
        html: receiptHtmlResponse.html,
        pdfSizing: receiptHtmlResponse.pdfSizing,
        fileName,
      });
      toast.success("Receipt PDF downloaded.");
    } catch (error) {
      const message = getReceiptErrorMessage(
        error,
        "Could not generate a receipt PDF.",
      );
      toast.error(message);
    } finally {
      setReceiptPdfGeneratingInvoiceId((current) =>
        current === invoice.providerInvoiceId ? null : current,
      );
    }
  }

  async function submitInvoicePayment() {
    if (!activePayInvoice) {
      return;
    }

    if (selectedCustomerId === null) {
      toast.error("Select a customer before submitting payment.");
      return;
    }

    if (paymentSourceMode === PaymentSourceMode.Saved) {
      if (!selectedSavedPaymentMethodId) {
        toast.error("Select a saved payment method.");
        return;
      }

      if (
        !allowUnknownCardTypeProcessing &&
        selectedSavedPaymentMethod?.type === UiPaymentMethodType.Card &&
        isUnknownCard(selectedSavedPaymentMethod.cardCategory)
      ) {
        toast.error(UNSUPPORTED_UNKNOWN_CARD_MESSAGE);
        return;
      }

      try {
        await executeInvoicePayment(
          InvoicePaymentMethodSourceType.SavedPaymentMethod,
        );
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : "Could not process this invoice payment.";
        toast.error(message);
      }
      return;
    }

    const normalizedBillingAddressForm = {
      ...billingAddressForm,
      postalCode: normalizePostalCode(billingAddressForm.postalCode),
    };

    updateBillingAddressForm(normalizedBillingAddressForm);

    const nextValidationErrors = validateBillingAddressFields(
      normalizedBillingAddressForm,
      paymentStates.length > 0,
    );

    if (newMethodType === PaymentMethodTypeEnum.Ach && newAchAccountType === null) {
      nextValidationErrors.achAccountType = "Account type is required."
    }

    if (newMethodType === PaymentMethodTypeEnum.Ach && newAchAccountHolderType === null) {
      nextValidationErrors.achAccountHolderType = "Account holder type is required."
    }

    if (Object.keys(nextValidationErrors).length > 0) {
      setBillingAddressValidationErrors(nextValidationErrors)
      toast.error(
        nextValidationErrors.achAccountType || nextValidationErrors.achAccountHolderType
          ? "Select the ACH account type and account holder type."
          : "Enter billing first name, last name, and postal code."
      )
      return
    }

    if (
      !allowUnknownCardTypeProcessing &&
      newMethodType === PaymentMethodTypeEnum.Card &&
      isUnknownCard(newMethodCardCategory)
    ) {
      setCollectError(UNSUPPORTED_UNKNOWN_CARD_MESSAGE);
      toast.error(UNSUPPORTED_UNKNOWN_CARD_MESSAGE);
      return;
    }

    setCollectError(null);
    setPaySubmitPending(true);
    clearPaySubmitTimeout();
    paySubmitTimeoutRef.current = window.setTimeout(() => {
      if (!paySubmitPendingRef.current) {
        return;
      }

      setPaySubmitPending(false);
      setCollectError(
        "Secure payment fields did not respond. Please check details and try again.",
      );
      toast.error(
        "Secure payment fields did not respond. Please check details and try again.",
      );
    }, PAY_REQUEST_TIMEOUT_MS);
    setCollectTokenRequestNonce((current) => current + 1);
  }

  async function onCollectTokenized(tokenized: CollectJsTokenizedPayment) {
    if (!paySubmitPendingRef.current) {
      return;
    }

    setPaySubmitPending(false);
    clearPaySubmitTimeout();

    const normalizedCategory = normalizeCardCategoryKnown(
      tokenized.cardCategory,
    );
    setNewMethodCardCategory(normalizedCategory);

    if (
      !allowUnknownCardTypeProcessing &&
      tokenized.paymentMethodType === PaymentMethodTypeEnum.Card &&
      isUnknownCard(normalizedCategory)
    ) {
      setCollectError(UNSUPPORTED_UNKNOWN_CARD_MESSAGE);
      toast.error(UNSUPPORTED_UNKNOWN_CARD_MESSAGE);
      return;
    }

    try {
      await executeInvoicePayment(
        InvoicePaymentMethodSourceType.NewPaymentMethod,
        tokenized.token,
        tokenized,
      );
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Could not process this invoice payment.";
      setCollectError(message);
      toast.error(message);
    }
  }

  function updateFilter(nextSubscriptionId: string, nextStatus: InvoiceFilter) {
    const nextParams = new URLSearchParams(searchParams);

    if (nextSubscriptionId === ALL_SUBSCRIPTIONS_FILTER) {
      nextParams.delete("subscription");
    } else {
      nextParams.set("subscription", nextSubscriptionId);
    }

    if (nextStatus === InvoiceFilter.All) {
      nextParams.delete("status");
    } else {
      nextParams.set("status", nextStatus);
    }

    setSearchParams(nextParams);
  }

  return (
    <section className="sm:rounded-lg sm:border sm:bg-card sm:p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold">Invoices</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Review all invoices or drill into invoices for one subscription.
          </p>
        </div>
      </div>

      <div className="mt-6 grid gap-3 md:grid-cols-2">
        <label className="flex flex-col gap-2 text-sm">
          <span className="font-medium">Subscription</span>
          <select
            value={selectedSubscriptionId}
            onChange={(event) =>
              updateFilter(event.target.value, selectedFilter)
            }
            className="h-8 rounded-lg border border-input bg-background px-2.5"
          >
            <option value={ALL_SUBSCRIPTIONS_FILTER}>All subscriptions</option>
            {subscriptions.map((subscription) => (
              <option key={subscription.id} value={subscription.id}>
                {subscription.name}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-2 text-sm">
          <span className="font-medium">Invoice status</span>
          <select
            value={selectedFilter}
            onChange={(event) =>
              updateFilter(
                selectedSubscriptionId,
                event.target.value as InvoiceFilter,
              )
            }
            className="h-8 rounded-lg border border-input bg-background px-2.5"
          >
            <option value={InvoiceFilter.All}>All statuses</option>
            <option value={InvoiceFilter.Open}>Open</option>
            <option value={InvoiceFilter.Overdue}>Overdue</option>
            <option value={InvoiceFilter.Paid}>Paid</option>
          </select>
        </label>
      </div>

      <div className="mt-6 overflow-x-auto rounded-lg border">
        <table className="w-full min-w-[860px] border-collapse text-sm">
          <thead className="bg-muted/40 text-left">
            <tr>
              <th className="px-4 py-3 font-medium">Invoice</th>
              <th className="px-4 py-3 font-medium">Subscription</th>
              <th className="px-4 py-3 font-medium">Period</th>
              <th className="px-4 py-3 font-medium">Issued</th>
              <th className="px-4 py-3 font-medium">Due</th>
              <th className="px-4 py-3 font-medium">Amount</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {selectedCustomerId === null ? (
              <tr className="border-t">
                <td
                  className="px-4 py-8 text-center text-sm text-muted-foreground"
                  colSpan={8}
                >
                  Select a customer to view invoices.
                </td>
              </tr>
            ) : null}

            {shouldShowTableLoading
              ? Array.from({ length: 6 }).map((_, index) => (
                  <tr key={`invoices-skeleton-${index}`} className="border-t">
                    <td className="px-4 py-3">
                      <Skeleton className="h-5 w-24" />
                    </td>
                    <td className="px-4 py-3">
                      <Skeleton className="h-5 w-32" />
                    </td>
                    <td className="px-4 py-3">
                      <Skeleton className="h-5 w-28" />
                    </td>
                    <td className="px-4 py-3">
                      <Skeleton className="h-5 w-20" />
                    </td>
                    <td className="px-4 py-3">
                      <Skeleton className="h-5 w-20" />
                    </td>
                    <td className="px-4 py-3">
                      <Skeleton className="h-5 w-24" />
                    </td>
                    <td className="px-4 py-3">
                      <Skeleton className="h-6 w-20 rounded-full" />
                    </td>
                    <td className="px-4 py-3">
                      <Skeleton className="h-8 w-44" />
                    </td>
                  </tr>
                ))
              : null}

            {selectedCustomerId !== null &&
            !shouldShowTableLoading &&
            filteredInvoices.length === 0 ? (
              <tr className="border-t">
                <td
                  className="px-4 py-8 text-center text-sm text-muted-foreground"
                  colSpan={8}
                >
                  {selectedSubscriptionId !== ALL_SUBSCRIPTIONS_FILTER ||
                  selectedFilter !== InvoiceFilter.All
                    ? "No invoices match the selected filters."
                    : "No invoices found for this customer."}
                </td>
              </tr>
            ) : null}

            {selectedCustomerId !== null && !shouldShowTableLoading
              ? filteredInvoices.map((invoice) => {
                  const statusDisplay = getInvoiceStatusDisplay(invoice);
                  const isGeneratingPdf =
                    pdfGeneratingInvoiceId === invoice.providerInvoiceId;
                  const isGeneratingReceiptPdf =
                    receiptPdfGeneratingInvoiceId === invoice.providerInvoiceId;
                  const isPaidInvoice = invoice.status === UiInvoiceStatus.Paid;

                  return (
                    <tr key={invoice.id} className="border-t">
                      <td className="px-4 py-3 font-medium">{invoice.id}</td>
                      <td className="px-4 py-3">{invoice.subscriptionName}</td>
                      <td className="px-4 py-3">{invoice.periodLabel}</td>
                      <td className="px-4 py-3">
                        {formatDateOrFallback(invoice.issueDate)}
                      </td>
                      <td className="px-4 py-3">
                        {formatDateOrFallback(invoice.dueDate)}
                      </td>
                      <td className="px-4 py-3">
                        {formatCurrency(invoice.amountCents)}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`rounded-full px-2 py-1 text-xs font-medium ${statusDisplay.className}`}
                        >
                          {statusDisplay.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {invoice.status === UiInvoiceStatus.Due ||
                          invoice.status === UiInvoiceStatus.Overdue ? (
                            <Button
                              size="sm"
                              onClick={() => openPayDrawer(invoice)}
                            >
                              Pay now
                            </Button>
                          ) : null}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                size="sm"
                                variant="outline"
                                title="Invoice actions"
                              >
                                Actions
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-52">
                              <DropdownMenuItem
                                onClick={() => openInvoiceView(invoice)}
                              >
                                View invoice
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => downloadInvoicePdf(invoice)}
                                disabled={isGeneratingPdf}
                              >
                                {isGeneratingPdf
                                  ? "Generating invoice PDF..."
                                  : "Download invoice PDF"}
                              </DropdownMenuItem>
                              {isPaidInvoice ? (
                                <>
                                  <DropdownMenuItem
                                    onClick={() => openReceiptView(invoice)}
                                  >
                                    View receipt
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => downloadReceiptPdf(invoice)}
                                    disabled={isGeneratingReceiptPdf}
                                  >
                                    {isGeneratingReceiptPdf
                                      ? "Generating receipt PDF..."
                                      : "Download receipt PDF"}
                                  </DropdownMenuItem>
                                </>
                              ) : null}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </td>
                    </tr>
                  );
                })
              : null}
          </tbody>
        </table>
      </div>

      <Sheet
        open={payInvoice !== null}
        onOpenChange={(open) => !open && resetPayDrawerState()}
      >
        <SheetContent
          side="right"
          className="w-full overflow-y-auto sm:max-w-xl"
        >
          <SheetHeader>
            <SheetTitle>Pay invoice</SheetTitle>
            <SheetDescription>
              Choose a saved payment method or pay with a new one securely
              through NMI.
            </SheetDescription>
          </SheetHeader>

          <div className="grid gap-3 px-4 pb-4">
            {isInvoiceDetailLoading || isInvoiceDetailFetching ? (
              <div className="rounded-md border bg-muted/20 p-3 space-y-2">
                <Skeleton className="h-5 w-40" />
                <Skeleton className="h-4 w-44" />
                <Skeleton className="h-4 w-36" />
              </div>
            ) : activePayInvoice ? (
              <div className="rounded-md border bg-muted/20 p-3 text-sm">
                <p className="font-medium">Invoice {activePayInvoice.id}</p>
                <p className="mt-1 text-muted-foreground">
                  Amount due: {formatCurrency(activePayInvoice.amountCents)}
                </p>
                <p className="mt-1 text-muted-foreground">
                  Due date: {formatDateOrFallback(activePayInvoice.dueDate)}
                </p>
              </div>
            ) : null}

            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium">Payment source</span>
              <select
                value={paymentSourceMode}
                onChange={(event) => {
                  setPaymentSourceMode(event.target.value as PaymentSourceMode);
                  setBillingAddressValidationErrors({});
                }}
                className="h-8 rounded-lg border border-input bg-background px-2.5"
              >
                <option
                  value={PaymentSourceMode.Saved}
                  disabled={savedPaymentMethods.length === 0}
                >
                  Saved payment method
                </option>
                <option value={PaymentSourceMode.New}>
                  New payment method
                </option>
              </select>
            </label>

            {paymentSourceMode === PaymentSourceMode.Saved ? (
              <>
                {savedPaymentMethods.length === 0 ? (
                  <p className="rounded-md border bg-muted/20 p-3 text-sm text-muted-foreground">
                    No enabled saved payment methods are available for this
                    customer.
                  </p>
                ) : (
                  <label className="flex flex-col gap-1 text-sm">
                    <span className="font-medium">Saved payment method</span>
                    <select
                      value={selectedSavedPaymentMethodId ?? ""}
                      onChange={(event) =>
                        setSelectedSavedPaymentMethodId(
                          Number(event.target.value),
                        )
                      }
                      className="h-8 w-full min-w-0 rounded-lg border border-input bg-background px-2.5 pr-10"
                    >
                      {savedPaymentMethods.map((method) => (
                        <option
                          key={method.id}
                          value={method.id}
                          className="whitespace-normal break-words"
                        >
                          {formatPaymentMethodSummary(method)}
                          {method.isDefault ? " (Default)" : ""}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
              </>
            ) : (
              <>
                <label className="flex flex-col gap-1 text-sm">
                  <span className="font-medium">Method type</span>
                  <select
                    value={newMethodType}
                    onChange={(event) => {
                      setNewMethodType(event.target.value as PaymentMethodTypeEnum)
                      setNewAchAccountType(null)
                      setNewAchAccountHolderType(null)
                      setBillingAddressValidationErrors({})
                    }}
                    className="h-8 rounded-lg border border-input bg-background px-2.5"
                  >
                    <option value={PaymentMethodTypeEnum.Card}>
                      Credit card
                    </option>
                    <option value={PaymentMethodTypeEnum.Ach}>
                      ACH bank account
                    </option>
                  </select>
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  <span className="font-medium">Nickname</span>
                  <input
                    id="nickname"
                    value={saveAndSetDefault ? (nickname ?? "") : ""}
                    onChange={(event) =>
                      setNickname(event.target.value?.slice(0, 30) ?? "")
                    }
                    disabled={!saveAndSetDefault}
                    className="h-8 rounded-lg border border-input bg-background px-2.5 disabled:cursor-not-allowed disabled:opacity-60"
                  />
                </label>

                <CollectJsPaymentForm
                  paymentMethodType={newMethodType}
                  config={tokenizationDetail ?? null}
                  tokenRequestNonce={collectTokenRequestNonce}
                  showSaveAndSetDefaultOption
                  saveAndSetDefault={saveAndSetDefault}
                  onSaveAndSetDefaultChange={setSaveAndSetDefault}
                  onCardCategoryChange={(category) =>
                    setNewMethodCardCategory(
                      normalizeCardCategoryKnown(category),
                    )
                  }
                  onReadyChange={() => undefined}
                  onTokenized={onCollectTokenized}
                  onError={(message) => {
                    clearPaySubmitTimeout();
                    setPaySubmitPending(false);
                    setCollectError(message);
                    toast.error(message);
                  }}
                />

                {newMethodType === PaymentMethodTypeEnum.Ach ? (
                  <AchPaymentFields
                    achAccountType={newAchAccountType}
                    achAccountHolderType={newAchAccountHolderType}
                    onChange={(nextValue) => {
                      setNewAchAccountType(nextValue.achAccountType)
                      setNewAchAccountHolderType(nextValue.achAccountHolderType)
                      setBillingAddressValidationErrors((current) => ({
                        ...current,
                        achAccountType: undefined,
                        achAccountHolderType: undefined,
                      }))
                    }}
                    accountTypeError={billingAddressValidationErrors.achAccountType}
                    accountHolderTypeError={billingAddressValidationErrors.achAccountHolderType}
                  />
                ) : null}

                {collectError ? <p className="text-sm text-destructive">{collectError}</p> : null}

                <BillingAddressFields
                  value={billingAddressForm}
                  onChange={updateBillingAddressForm}
                  validationErrors={billingAddressValidationErrors}
                  countryOptions={paymentCountries}
                  stateOptions={paymentStates}
                  isCountriesLoading={
                    paymentCountriesLoading || paymentCountriesFetching
                  }
                  isStatesLoading={
                    paymentStatesLoading || paymentStatesFetching
                  }
                />
              </>
            )}

            {isAchPaymentSelected ? (
              <div className="space-y-2 rounded-md border bg-muted/20 p-3 text-sm text-muted-foreground">
                <p>
                  By clicking 'Pay Invoice' I authorize Cloudfit Software, LLC
                  to debit my bank account {formatCurrency(totalChargeCents)} on{" "}
                  {currentDateLabel}.
                </p>
                {shouldShowAchRecurringAuthorization ? (
                  <p>
                    Additionally, you authorize CloudFit Software, LLC to
                    electronically debit the bank account you have provided for
                    invoices due on your account, in the amount and on the due
                    date shown on each invoice, and to re-initiate any debit
                    returned for insufficient funds. This authorization remains
                    in effect until you revoke it. You may revoke it at any time
                    by either changing your payment method within the Portal or
                    by contacting payments@cloudfitsoftware.com, provided we
                    receive your request at least 2 business days before the
                    next scheduled debit.
                  </p>
                ) : null}
              </div>
            ) : null}

            <div className="rounded-md border bg-muted/20 p-3 text-sm">
              <div className="flex items-center justify-between gap-3">
                <span className="text-muted-foreground">Amount Due</span>
                <span>{formatCurrency(amountDueCents)}</span>
              </div>
              {shouldApplySurcharge && surchargeAmountCents > 0 ? (
                <div className="mt-1 flex items-center justify-between gap-3">
                  <span className="text-muted-foreground">
                    Card Surcharge ({surchargePercent ?? 0}%)
                  </span>
                  <span>{formatCurrency(surchargeAmountCents)}</span>
                </div>
              ) : null}
              <div className="mt-2 flex items-center justify-between gap-3 border-t pt-2 font-medium">
                <span>Total Charge</span>
                <span>{formatCurrency(totalChargeCents)}</span>
              </div>
            </div>
          </div>

          <SheetFooter className="border-t">
            <Button
              onClick={submitInvoicePayment}
              disabled={
                isPayingInvoice ||
                tokenizationLoading ||
                (paymentSourceMode === PaymentSourceMode.Saved &&
                  !selectedSavedPaymentMethod) ||
                paySubmitPending
              }
            >
              {isPayingInvoice || paySubmitPending
                ? "Processing..."
                : "Pay invoice"}
            </Button>
            <Button variant="ghost" onClick={resetPayDrawerState}>
              Cancel
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </section>
  );
}
